import React, { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { crud, hd, errorMessage } from "../api";
import { PresenceDot } from "../ui";
import { useCrud } from "./common";
import { T, TYPE, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };
const STRATEGIES = [
  ["round_robin", "Round robin", "Rotate through available agents"],
  ["load_based", "Load based", "Agent with the fewest open items"],
  ["skill_based", "Skill based", "Best skill match for the category, then least loaded"],
  ["manual", "Manual", "Nobody is auto-assigned"],
];

export const TeamsEditor = () => {
  const ctx = useHd();
  const { rows, create, update, remove } = useCrud("/teams");
  const [edit, setEdit] = useState(null);
  if (!rows) return <CircularProgress size={22} />;
  const save = async () => {
    const body = { name: edit.name, description: edit.description, members: edit.members.map((m) => String(m.id || m)), skills: edit.skills, assignmentStrategy: edit.assignmentStrategy, isActive: edit.isActive };
    const ok = edit._id ? await update(edit._id, body) : await create(body);
    if (ok) setEdit(null);
  };
  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ name: "", description: "", members: [], skills: [], assignmentStrategy: "round_robin", isActive: true })}>Add team</Button>
      </Stack>
      <Table size="small">
        <TableHead><TableRow sx={th}><TableCell>Team</TableCell><TableCell>Members</TableCell><TableCell>Assignment</TableCell><TableCell>Skills</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t._id}>
              <TableCell sx={{ fontWeight: 600 }}>{t.name}{!t.isActive && " (inactive)"}</TableCell>
              <TableCell>{t.members.map((m) => ctx.agentById.get(String(m.id))?.name || "?").join(", ") || "—"}</TableCell>
              <TableCell>{STRATEGIES.find((s) => s[0] === t.assignmentStrategy)?.[1]}</TableCell>
              <TableCell>{(t.skills || []).join(", ")}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => setEdit({ ...t })}><Pencil size={15} /></IconButton>
                <IconButton size="small" onClick={() => remove(t._id)}><Trash2 size={15} color="#B42318" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && <TableRow><TableCell colSpan={5} sx={{ color: T.inkMuted }}>No teams yet. Teams route tickets and chats to the right agents.</TableCell></TableRow>}
        </TableBody>
      </Table>
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{edit._id ? "Edit team" : "New team"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField fullWidth label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Description" value={edit.description} onChange={(e) => setEdit((p) => ({ ...p, description: e.target.value }))} /></Grid>
              <Grid item xs={12}>
                <Autocomplete multiple options={ctx.agents} getOptionLabel={(a) => a.name} value={ctx.agents.filter((a) => edit.members.some((m) => String(m.id || m) === String(a.memberId)))}
                  onChange={(_, v) => setEdit((p) => ({ ...p, members: v.map((a) => ({ kind: a.kind, id: a.memberId })) }))} renderInput={(p) => <TextField {...p} label="Members" />} />
              </Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Assignment strategy" value={edit.assignmentStrategy} onChange={(e) => setEdit((p) => ({ ...p, assignmentStrategy: e.target.value }))}>
                  {STRATEGIES.map(([k, l, d]) => <MenuItem key={k} value={k}><Box><Typography sx={{ fontSize: TYPE.small }}>{l}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{d}</Typography></Box></MenuItem>)}
                </TextField>
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 0.5 }}>Automatic strategies only pick agents who are online (chat) or online/away (tickets) and under their capacity.</Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete multiple freeSolo options={[]} value={edit.skills || []} onChange={(_, v) => setEdit((p) => ({ ...p, skills: v }))} renderInput={(p) => <TextField {...p} label="Team skills" placeholder="billing, technical…" />} />
              </Grid>
              <Grid item xs={12}><Checkbox checked={edit.isActive} onChange={(e) => setEdit((p) => ({ ...p, isActive: e.target.checked }))} /> Active</Grid>
            </Grid>
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export const AgentsEditor = () => {
  const ctx = useHd();
  const [rows, setRows] = useState(null);
  const [roles, setRoles] = useState([]);
  const load = async () => {
    try {
      const [a, r] = await Promise.all([hd.agents(), crud("/roles").list()]);
      setRows(a);
      setRoles(r);
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = async (memberId, body) => {
    try {
      await hd.updateAgent(memberId, body);
      ctx.notify("Agent updated");
      load();
      ctx.reload();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  if (!rows) return <CircularProgress size={22} />;
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>Agents are your workspace owner and CRM team members. Add people in <b>Team Members</b>; set their helpdesk role, teams and capacity here.</Alert>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead><TableRow sx={th}><TableCell>Agent</TableCell><TableCell>Role</TableCell><TableCell>Teams</TableCell><TableCell>Skills</TableCell><TableCell>Languages</TableCell><TableCell>Max chats</TableCell><TableCell>Max tickets</TableCell><TableCell>Active</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a._id}>
                <TableCell><Stack direction="row" spacing={1} alignItems="center"><PresenceDot presence={a.presence} /><Box><Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{a.name}{a.kind === "owner" ? " (owner)" : ""}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{a.email}</Typography></Box></Stack></TableCell>
                <TableCell>
                  <TextField select size="small" value={a.roleId || ""} disabled={a.kind === "owner"} onChange={(e) => upd(a.memberId, { roleId: e.target.value })} sx={{ minWidth: 150 }}>
                    {roles.map((r) => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Autocomplete multiple size="small" options={ctx.teams} getOptionLabel={(t) => t.name} value={ctx.teams.filter((t) => (a.teamIds || []).map(String).includes(String(t._id)))}
                    onChange={(_, v) => upd(a.memberId, { teamIds: v.map((t) => t._id) })} renderInput={(p) => <TextField {...p} />} />
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Autocomplete multiple freeSolo size="small" options={[]} value={a.skills || []} onChange={(_, v) => upd(a.memberId, { skills: v })} renderInput={(p) => <TextField {...p} />} />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  <Autocomplete multiple size="small" options={["en", "hi", "hinglish", "ar"]} value={a.languages || []} onChange={(_, v) => upd(a.memberId, { languages: v })} renderInput={(p) => <TextField {...p} />} />
                </TableCell>
                <TableCell><TextField size="small" type="number" defaultValue={a.maxConcurrentChats} onBlur={(e) => Number(e.target.value) !== a.maxConcurrentChats && upd(a.memberId, { maxConcurrentChats: e.target.value })} sx={{ width: 80 }} /></TableCell>
                <TableCell><TextField size="small" type="number" defaultValue={a.maxOpenTickets} onBlur={(e) => Number(e.target.value) !== a.maxOpenTickets && upd(a.memberId, { maxOpenTickets: e.target.value })} sx={{ width: 80 }} /></TableCell>
                <TableCell><Checkbox checked={a.isActive} disabled={a.kind === "owner"} onChange={(e) => upd(a.memberId, { isActive: e.target.checked })} />{!a.crmActive && <Chip size="small" label="removed from CRM" />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

const GROUPS = [
  ["Tickets", ["tickets.view", "tickets.view_all", "tickets.create", "tickets.update", "tickets.delete", "tickets.assign", "tickets.reply", "tickets.resolve", "tickets.close", "tickets.reopen", "tickets.merge", "notes.internal"]],
  ["Chat", ["chat.view", "chat.reply", "chat.assign", "chat.transfer", "chat.takeover"]],
  ["AI", ["ai.use", "ai.configure", "ai.auto_reply"]],
  ["SLA & automation", ["sla.view", "sla.manage", "automation.view", "automation.manage"]],
  ["Knowledge & reports", ["knowledge.view", "knowledge.manage", "reports.view", "reports.export"]],
  ["Admin", ["settings.manage"]],
];

export const RolesEditor = () => {
  const { rows, create, update, remove } = useCrud("/roles");
  const [newName, setNewName] = useState("");
  const [draft, setDraft] = useState({});
  if (!rows) return <CircularProgress size={22} />;
  const perms = (r) => draft[r._id] || r.permissions;
  const toggle = (r, p) => {
    const cur = new Set(perms(r));
    cur.has(p) ? cur.delete(p) : cur.add(p);
    setDraft((d) => ({ ...d, [r._id]: [...cur] }));
  };
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="New role name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button variant="contained" sx={primaryButtonSx} onClick={async () => { if (newName.trim() && (await create({ name: newName.trim(), permissions: ["tickets.view"] }))) setNewName(""); }}>Add role</Button>
      </Stack>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={th}>
              <TableCell>Permission</TableCell>
              {rows.map((r) => (
                <TableCell key={r._id} align="center">
                  {r.name}
                  {!r.isSystem && <IconButton size="small" onClick={() => remove(r._id)}><Trash2 size={13} color="#B42318" /></IconButton>}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {GROUPS.map(([g, list]) => (
              <React.Fragment key={g}>
                <TableRow><TableCell colSpan={rows.length + 1} sx={{ bgcolor: T.surfaceMuted, fontWeight: 700, fontSize: TYPE.micro }}>{g}</TableCell></TableRow>
                {list.map((p) => (
                  <TableRow key={p}>
                    <TableCell sx={{ fontSize: TYPE.small, fontFamily: "monospace" }}>{p}</TableCell>
                    {rows.map((r) => <TableCell key={r._id} align="center"><Checkbox size="small" checked={perms(r).includes(p)} disabled={r.key === "admin"} onChange={() => toggle(r, p)} /></TableCell>)}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
            <TableRow>
              <TableCell />
              {rows.map((r) => (
                <TableCell key={r._id} align="center">
                  {draft[r._id] && <Button size="small" variant="contained" sx={primaryButtonSx} onClick={async () => { if (await update(r._id, { permissions: draft[r._id] })) setDraft((d) => { const n = { ...d }; delete n[r._id]; return n; }); }}>Save</Button>}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>Without <b>tickets.view_all</b> an agent sees tickets assigned to them, to their teams, and unassigned ones. The workspace owner always has full access.</Typography>
    </Box>
  );
};
